import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Block,
  DeleteBlockResult,
  DuplicateBlockResult,
  GenerateUnitsResult,
  Page,
  Unit,
} from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  LoadingRows,
  PageHeader,
  Select,
  StatCard,
  StatusBadge,
  TextInput,
  cx,
} from '../components/ui.js';
import { IconClose } from '../components/icons.js';

/**
 * Estrutura fisica do condominio.
 *
 * A tela e organizada na ordem em que o predio e montado — blocos primeiro,
 * unidades depois — porque e assim que o sindico pensa ao cadastrar. O gerador
 * fica em destaque: cadastrar 200 apartamentos um a um nao acontece, e quem
 * chega aqui na primeira vez esta com o predio inteiro pela frente.
 */
export function UnitsPage() {
  const { allows } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const canCreate = allows('condominio:unidades:criar');
  const canEdit = allows('condominio:unidades:editar');
  const canArchive = allows('condominio:unidades:excluir');

  // Arquivado fica escondido por padrao — era exatamente o contrario que
  // fazia o arquivamento parecer nao funcionar: o bloco continuava na lista,
  // agora com "0 unidades", e quem arquivou concluia que nada acontecera.
  const [showArchived, setShowArchived] = useState(false);

  const { data: blocks, isLoading: loadingBlocks } = useQuery({
    queryKey: ['blocks', showArchived],
    queryFn: () =>
      api.get<{ items: Block[] }>(`/blocks${showArchived ? '?arquivados=true' : ''}`),
  });

  const { data: units, isLoading: loadingUnits } = useQuery({
    queryKey: ['units', 'structure'],
    queryFn: () => api.get<Page<Unit>>('/units?limit=500'),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['blocks'] });
    void queryClient.invalidateQueries({ queryKey: ['units'] });
  }

  function fail(err: unknown) {
    setError(err instanceof ApiRequestError ? err.message : 'Nao foi possivel concluir.');
  }

  const blockItems = blocks?.items ?? [];
  const unitItems = units?.items ?? [];
  // Os cartoes de topo e os seletores falam de estrutura VIVA: um bloco
  // arquivado nao recebe unidade nova nem encomenda.
  const activeBlocks = blockItems.filter((block) => block.status === 'active');

  return (
    <>
      <PageHeader title="Blocos e unidades" />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Blocos" value={activeBlocks.length} hint="ativos" />
        <StatCard label="Unidades" value={unitItems.length} hint="cadastradas" />
        <StatCard
          label="Moradores vinculados"
          value={unitItems.reduce((sum, unit) => sum + unit.residentCount, 0)}
          hint="em todas as unidades"
          highlight
        />
      </div>

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <BlocksCard
          blocks={blockItems}
          loading={loadingBlocks}
          canCreate={canCreate}
          canEdit={canEdit}
          canArchive={canArchive}
          showArchived={showArchived}
          onToggleArchived={() => setShowArchived((value) => !value)}
          onDone={refresh}
          onError={fail}
          clearError={() => setError(null)}
        />

        <UnitsCard
          blocks={activeBlocks}
          units={unitItems}
          loading={loadingUnits}
          canCreate={canCreate}
          canEdit={canEdit}
          canArchive={canArchive}
          onDone={refresh}
          onError={fail}
          clearError={() => setError(null)}
        />
      </div>

      {canCreate && (
        <GeneratorCard
          blocks={activeBlocks}
          onDone={refresh}
          onError={fail}
          clearError={() => setError(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

interface SectionProps {
  onDone: () => void;
  onError: (error: unknown) => void;
  clearError: () => void;
}

function BlocksCard({
  blocks,
  loading,
  canCreate,
  canEdit,
  canArchive,
  showArchived,
  onToggleArchived,
  onDone,
  onError,
  clearError,
}: SectionProps & {
  blocks: Block[];
  loading: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canArchive: boolean;
  showArchived: boolean;
  onToggleArchived: () => void;
}) {
  const [name, setName] = useState('');
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [erasing, setErasing] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post<Block>('/blocks', { name }),
    onSuccess: () => {
      setName('');
      onDone();
    },
    onError,
  });

  const archive = useMutation({
    mutationFn: (block: Block) =>
      api.delete<void>(
        // A cascata vai explicita na URL: o backend recusa arquivar bloco com
        // unidade se ninguem pedir, e e essa recusa que protege quem chama a
        // API sem passar por esta tela.
        `/blocks/${block.id}${block.unitCount > 0 ? '?comUnidades=true' : ''}`,
      ),
    onSuccess: () => {
      setConfirming(null);
      onDone();
    },
    onError,
  });

  const restore = useMutation({
    mutationFn: (block: Block) => api.post<void>(`/blocks/${block.id}/restaurar`),
    onSuccess: onDone,
    onError,
  });

  /**
   * Apagar de vez — o caso do bloco criado errado e da importacao que deu
   * errado, que arquivar nao resolve: arquivado, aquele lixo fica para sempre
   * na lista de arquivados e o sindico convive com um cadastro que ele sabe
   * estar errado.
   *
   * A API recusa se houver UMA encomenda no historico do bloco, e a mensagem
   * dela e que aparece na tela — nao ha como decidir isso aqui, o front nao
   * sabe o historico.
   */
  const erase = useMutation({
    mutationFn: (block: Block) =>
      api.delete<DeleteBlockResult>(`/blocks/${block.id}/definitivo`),
    onSuccess: () => {
      setErasing(null);
      setConfirming(null);
      onDone();
    },
    onError,
  });

  return (
    <Card>
      <CardHeader
        title="Blocos"
        subtitle="Torres, alas ou blocos. Predio sem divisao nao precisa de nenhum."
        action={
          <Button variant="quiet" className="py-1.5" onClick={onToggleArchived}>
            {showArchived ? 'So os ativos' : 'Ver arquivados'}
          </Button>
        }
      />

      {loading ? (
        <LoadingRows rows={2} />
      ) : blocks.length === 0 ? (
        <EmptyState
          title={showArchived ? 'Nenhum bloco, nem arquivado' : 'Nenhum bloco'}
          description="As unidades ficam direto no condominio. Crie um bloco se o predio tem torres separadas."
        />
      ) : (
        <ul className="divide-y divide-line-soft">
          {blocks.map((block) => (
            <li key={block.id} className="px-5 py-3">
              <div className="flex items-center justify-between gap-3">
                <span>
                  <span className="flex items-center gap-2 text-sm font-bold">
                    {block.name}
                    {block.status === 'archived' && (
                      <StatusBadge tone="warn">Arquivado</StatusBadge>
                    )}
                  </span>
                  <span className="block text-xs text-muted">
                    {block.unitCount} unidade{block.unitCount === 1 ? '' : 's'}
                    {block.status === 'archived' && ' ativas — as arquivadas voltam junto'}
                  </span>
                </span>

                <span className="flex shrink-0 items-center gap-1">
                  {/* Bloco arquivado nao duplica nem arquiva de novo: as duas
                      unicas saidas dele sao voltar ao mapa ou sair de vez. */}
                  {block.status === 'archived' ? (
                    <>
                      {canEdit && (
                        <Button
                          variant="quiet"
                          loading={restore.isPending && restore.variables?.id === block.id}
                          onClick={() => {
                            clearError();
                            restore.mutate(block);
                          }}
                        >
                          Restaurar
                        </Button>
                      )}
                      {canArchive && (
                        <Button
                          variant="quiet"
                          onClick={() => {
                            clearError();
                            setErasing(erasing === block.id ? null : block.id);
                          }}
                        >
                          Excluir de vez
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Duplicar so faz sentido com o bloco ja montado — e o
                          que vai ser copiado. */}
                      {canCreate && block.unitCount > 0 && (
                        <Button
                          variant="quiet"
                          onClick={() => {
                            clearError();
                            setDuplicating(duplicating === block.id ? null : block.id);
                          }}
                        >
                          {duplicating === block.id ? 'Fechar' : 'Duplicar'}
                        </Button>
                      )}

                      {canArchive && (
                        <Button
                          variant="quiet"
                          onClick={() => {
                            clearError();
                            setConfirming(confirming === block.id ? null : block.id);
                          }}
                        >
                          Arquivar
                        </Button>
                      )}
                    </>
                  )}
                </span>
              </div>

              {/* Excluir de vez. Painel proprio, e nao mais um botao dentro do
                  de arquivar: as duas operacoes se parecem na tela e nao se
                  parecem em nada no resultado, e a que nao tem volta nao pode
                  ser a mais facil de acertar sem querer. */}
              {erasing === block.id && (
                <div className="mt-3 rounded-2xl border border-danger/40 bg-danger/10 p-4">
                  <p className="text-sm font-semibold">Apagar {block.name} de vez?</p>
                  <p className="mt-1 text-sm text-muted">
                    O bloco e as unidades dele saem do banco — nao ficam arquivados, nao tem
                    desfazer. Os moradores continuam cadastrados, sem unidade, prontos para serem
                    vinculados ao bloco certo. Se alguma unidade ja recebeu encomenda, a operacao
                    e recusada: nesse caso o certo e arquivar.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      className="py-2"
                      loading={erase.isPending}
                      onClick={() => erase.mutate(block)}
                    >
                      Apagar {block.name} de vez
                    </Button>
                    <Button variant="ghost" className="py-2" onClick={() => setErasing(null)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* Arquivar 40 apartamentos de uma vez pede uma pergunta, nao um
                  clique: e a operacao que desfaz uma duplicacao errada, mas
                  tambem a que apaga uma torre inteira do mapa por engano. */}
              {confirming === block.id && (
                <div className="mt-3 rounded-2xl border border-warn/40 bg-warn/10 p-4">
                  <p className="text-sm font-semibold">
                    Arquivar {block.name}
                    {block.unitCount > 0 && ` e as ${block.unitCount} unidades dele`}?
                  </p>
                  <p className="mt-1 text-sm text-muted">
                    {block.unitCount > 0
                      ? 'As unidades saem do mapa e do seletor de encomenda. O historico de cada uma continua guardado, e nada e apagado — mas os moradores vinculados ficam sem unidade.'
                      : 'O bloco sai da lista. Nada e apagado.'}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      className="py-2"
                      loading={archive.isPending}
                      onClick={() => archive.mutate(block)}
                    >
                      {block.unitCount > 0
                        ? `Arquivar bloco e ${block.unitCount} unidades`
                        : 'Arquivar bloco'}
                    </Button>
                    <Button variant="ghost" className="py-2" onClick={() => setConfirming(null)}>
                      Cancelar
                    </Button>
                  </div>

                  {/* A saida para "criei esse bloco errado" e "a importacao
                      trouxe torre que nao existe": arquivar guardaria para
                      sempre um cadastro que ninguem quer ver de novo. */}
                  <button
                    type="button"
                    onClick={() => {
                      setConfirming(null);
                      setErasing(block.id);
                    }}
                    className="mt-3 text-sm font-semibold text-faint underline underline-offset-4 hover:text-danger"
                  >
                    Este bloco nao deveria existir — apagar de vez
                  </button>
                </div>
              )}

              {duplicating === block.id && (
                <DuplicateBlockForm
                  block={block}
                  onDone={() => {
                    setDuplicating(null);
                    onDone();
                  }}
                  onError={onError}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {canCreate && (
        <form
          className="flex flex-wrap items-end gap-3 border-t border-line-soft p-5"
          onSubmit={(event) => {
            event.preventDefault();
            clearError();
            create.mutate();
          }}
        >
          <div className="min-w-40 flex-1">
            <Field label="Novo bloco">
              <TextInput
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Torre A"
                maxLength={32}
              />
            </Field>
          </div>
          <Button type="submit" loading={create.isPending} disabled={name.trim().length === 0}>
            Adicionar
          </Button>
        </form>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

/**
 * "Sete torres iguais" e o condominio brasileiro tipico.
 *
 * Rodar o gerador uma vez por torre funciona so enquanto todas forem o padrao
 * — a cobertura ou a sala do terreo que alguem acrescentou a mao nao se repete
 * sozinha. Duplicar copia o bloco COMO ELE ESTA, e a previa mostra exatamente
 * o que sera criado antes de gravar, porque 280 apartamentos errados nao tem
 * desfazer barato.
 */
function DuplicateBlockForm({
  block,
  onDone,
  onError,
}: {
  block: Block;
  onDone: () => void;
  onError: (error: unknown) => void;
}) {
  const [names, setNames] = useState('');
  const [preview, setPreview] = useState<DuplicateBlockResult | null>(null);

  // "Torre B, Torre C" -> ["Torre B", "Torre C"]. A virgula e o separador que
  // a pessoa ja usa quando escreve a lista das torres.
  const parsed = names
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  function send(previewOnly: boolean) {
    return api.post<DuplicateBlockResult>(`/blocks/${block.id}/duplicar`, {
      names: parsed,
      previewOnly,
    });
  }

  const simulate = useMutation({
    mutationFn: () => send(true),
    onSuccess: setPreview,
    onError,
  });

  const commit = useMutation({
    mutationFn: () => send(false),
    onSuccess: onDone,
    onError,
  });

  return (
    <div className="mt-3 rounded-2xl border border-line bg-surface-2 p-4">
      <Field label={`Novos blocos, iguais a ${block.name}`}>
        <TextInput
          value={names}
          onChange={(event) => {
            setNames(event.target.value);
            setPreview(null);
          }}
          placeholder="Torre B, Torre C, Torre D"
          autoFocus
        />
      </Field>

      <p className="mt-2 text-xs text-muted">
        Separe por virgula. Cada bloco recebe uma copia das {block.unitCount} unidades de{' '}
        {block.name}.
      </p>

      {preview && (
        <div className="mt-3 rounded-xl border border-accent/30 bg-accent/5 p-3">
          <p className="text-sm font-bold">
            {preview.blocks.length} bloco{preview.blocks.length === 1 ? '' : 's'} e{' '}
            {preview.unitsCreated} unidade{preview.unitsCreated === 1 ? '' : 's'}
          </p>
          <p className="mt-1 text-xs text-muted">
            {preview.blocks.join(', ')} — cada um com {preview.labels.length} unidades:{' '}
            {preview.labels.slice(0, 8).join(', ')}
            {preview.labels.length > 8 && '...'}
          </p>
          {preview.remainingInPlan !== null && (
            <p className="mt-1 text-xs text-faint">
              Sobram {preview.remainingInPlan} unidades no plano depois disto.
            </p>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          variant="ghost"
          className="py-2"
          loading={simulate.isPending}
          disabled={parsed.length === 0}
          onClick={() => simulate.mutate()}
        >
          Ver previa
        </Button>

        <Button
          className="py-2"
          loading={commit.isPending}
          disabled={!preview}
          onClick={() => commit.mutate()}
        >
          {preview ? `Criar ${preview.unitsCreated} unidades` : 'Duplicar'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Corrige uma unidade: numero, andar e em qual bloco ela fica.
 *
 * Mover de bloco esta aqui, e nao numa acao separada, porque no banco e a
 * mesma coluna e no balcao e o mesmo problema: a torre inteira foi importada
 * no bloco errado. O que a API pode recusar e a colisao — o "101" ja existe no
 * bloco de destino — e a mensagem dela diz exatamente isso.
 *
 * O historico acompanha: encomenda antiga aponta para a unidade, nao para o
 * texto do numero. Corrigir a placa da porta nao muda quem morava atras dela.
 */
function EditUnitForm({
  unit,
  blocks,
  onDone,
  onError,
  onCancel,
}: {
  unit: Unit;
  blocks: Block[];
  onDone: () => void;
  onError: (error: unknown) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(unit.label);
  const [blockId, setBlockId] = useState(unit.blockId ?? '');
  const [floor, setFloor] = useState(unit.floor === null ? '' : String(unit.floor));

  const save = useMutation({
    mutationFn: () =>
      api.patch<void>(`/units/${unit.id}`, {
        label: label.trim(),
        // `null` tira do bloco; um id move para la. Predio com blocos nao
        // aceita unidade solta, e quem recusa isso e o backend.
        blockId: blockId || null,
        floor: floor === '' ? null : Number(floor),
      }),
    onSuccess: onDone,
    onError,
  });

  return (
    <form
      className="mt-4 rounded-2xl border border-accent/40 bg-accent/5 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm font-bold">
        Editar {unit.block ? `${unit.block} · ` : ''}
        {unit.label}
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="w-24">
          <Field label="Numero">
            <TextInput
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              maxLength={32}
              autoFocus
            />
          </Field>
        </div>

        {blocks.length > 0 && (
          <div className="min-w-40">
            <Field label="Bloco">
              <Select
                value={blockId}
                onChange={setBlockId}
                emptyOption="Sem bloco"
                options={blocks.map((block) => ({ value: block.id, label: block.name }))}
              />
            </Field>
          </div>
        )}

        <div className="w-24">
          <Field label="Andar">
            <TextInput
              value={floor}
              onChange={(event) => setFloor(event.target.value.replace(/[^\d-]/g, ''))}
              inputMode="numeric"
              placeholder="Opcional"
            />
          </Field>
        </div>

        <Button type="submit" loading={save.isPending} disabled={label.trim().length === 0}>
          Salvar
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>

      {unit.residentCount > 0 && (
        <p className="mt-3 text-xs text-muted">
          {unit.residentCount} morador(es) vinculado(s) continuam nesta unidade — corrigir o numero
          nao desfaz vinculo nenhum.
        </p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------

function UnitsCard({
  blocks,
  units,
  loading,
  canCreate,
  canEdit,
  canArchive,
  onDone,
  onError,
  clearError,
}: SectionProps & {
  blocks: Block[];
  units: Unit[];
  loading: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canArchive: boolean;
}) {
  const [label, setLabel] = useState('');
  const [blockId, setBlockId] = useState('');
  const [floor, setFloor] = useState('');
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Unit | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/units', {
        label,
        ...(blockId ? { blockId } : {}),
        ...(floor ? { floor: Number(floor) } : {}),
      }),
    onSuccess: () => {
      setLabel('');
      setFloor('');
      onDone();
    },
    onError,
  });

  const archiveUnit = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/units/${id}`),
    onSuccess: onDone,
    onError,
  });

  const visible = filter ? units.filter((unit) => unit.blockId === filter) : units;

  return (
    <Card>
      <CardHeader
        title="Unidades"
        subtitle="Apartamentos, salas ou casas. E o que aparece no registro de encomenda."
        action={
          blocks.length > 0 ? (
            <Select
              value={filter}
              onChange={setFilter}
              emptyOption="Todos os blocos"
              options={blocks.map((block) => ({
                value: block.id,
                label: block.name,
                hint: `${block.unitCount}`,
              }))}
              aria-label="Filtrar unidades por bloco"
              className="min-w-44"
            />
          ) : undefined
        }
      />

      {loading ? (
        <LoadingRows rows={3} />
      ) : visible.length === 0 ? (
        <EmptyState
          title="Nenhuma unidade"
          description="Use o gerador abaixo para criar o predio inteiro de uma vez."
        />
      ) : (
        <div className="max-h-80 overflow-y-auto px-5 py-3">
          <div className="flex flex-wrap gap-2">
            {visible.map((unit) => (
              <span
                key={unit.id}
                className={cx(
                  'inline-flex items-center gap-1.5 rounded-pill border py-1 pl-1.5 pr-1.5 text-xs font-semibold',
                  editing?.id === unit.id
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line bg-surface-2',
                )}
                title={`${unit.residentCount} morador(es)`}
              >
                {/* Clicar no numero abre a edicao. Um apartamento digitado
                    "011" em vez de "101" so tinha duas saidas antes: arquivar
                    e recriar, perdendo os moradores vinculados, ou SQL. */}
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => {
                    clearError();
                    setEditing(editing?.id === unit.id ? null : unit);
                  }}
                  className="rounded-pill px-1.5 py-0.5 disabled:cursor-default"
                >
                  {unit.block && <span className="text-faint">{unit.block} · </span>}
                  {unit.label}
                </button>

                {/* Arquivar a unidade sozinha nao existia na tela: dava para
                    criar 200 de uma vez e nenhuma de volta. O "x" fica dentro
                    da propria etiqueta porque e o objeto que ele remove. */}
                {canArchive && (
                  <button
                    type="button"
                    aria-label={`Arquivar unidade ${unit.label}`}
                    disabled={archiveUnit.isPending && archiveUnit.variables === unit.id}
                    onClick={() => {
                      clearError();
                      archiveUnit.mutate(unit.id);
                    }}
                    className="rounded-full p-0.5 text-faint transition hover:bg-surface hover:text-danger disabled:opacity-40"
                  >
                    <IconClose className="size-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>

          {editing && (
            <EditUnitForm
              unit={editing}
              blocks={blocks}
              onDone={() => {
                setEditing(null);
                onDone();
              }}
              onError={onError}
              onCancel={() => setEditing(null)}
            />
          )}
        </div>
      )}

      {canCreate && (
        <form
          className="flex flex-wrap items-end gap-3 border-t border-line-soft p-5"
          onSubmit={(event) => {
            event.preventDefault();
            clearError();
            create.mutate();
          }}
        >
          <div className="w-24">
            <Field label="Unidade">
              <TextInput
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="804"
                maxLength={32}
              />
            </Field>
          </div>

          {/* Sem `emptyOption`: predio com torres nao tem unidade solta, e
              oferecer "Sem bloco" era como o 101 ia parar em lugar nenhum. */}
          {blocks.length > 0 && (
            <div className="min-w-32 flex-1">
              <Field label="Bloco">
                <Select
                  value={blockId}
                  onChange={setBlockId}
                  placeholder="Escolha o bloco..."
                  options={blocks.map((block) => ({ value: block.id, label: block.name }))}
                />
              </Field>
            </div>
          )}

          <div className="w-20">
            <Field label="Andar">
              <TextInput
                value={floor}
                onChange={(event) => setFloor(event.target.value)}
                inputMode="numeric"
                placeholder="8"
              />
            </Field>
          </div>

          <Button
            type="submit"
            loading={create.isPending}
            // Bloco obrigatorio quando o predio tem blocos — a API recusa, e
            // o botao desligado explica antes de a pessoa tentar.
            disabled={label.trim().length === 0 || (blocks.length > 0 && !blockId)}
          >
            Adicionar
          </Button>
        </form>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function GeneratorCard({
  blocks,
  onDone,
  onError,
  clearError,
}: SectionProps & { blocks: Block[] }) {
  const [blockId, setBlockId] = useState('');
  const [floorFrom, setFloorFrom] = useState('1');
  const [floorTo, setFloorTo] = useState('10');
  const [perFloor, setPerFloor] = useState('4');
  const [numbering, setNumbering] = useState<'floor_position' | 'sequential'>('floor_position');
  const [preview, setPreview] = useState<GenerateUnitsResult | null>(null);

  // Predio com torres nao aceita unidade solta: sem o bloco escolhido, os 200
  // apartamentos nasceriam em lugar nenhum. A API tambem recusa.
  const missingBlock = blocks.length > 0 && !blockId;

  function payload(previewOnly: boolean) {
    return {
      ...(blockId ? { blockId } : {}),
      floorFrom: Number(floorFrom),
      floorTo: Number(floorTo),
      unitsPerFloor: Number(perFloor),
      numbering,
      previewOnly,
    };
  }

  const simulate = useMutation({
    mutationFn: () => api.post<GenerateUnitsResult>('/units/generate', payload(true)),
    onSuccess: setPreview,
    onError,
  });

  const commit = useMutation({
    mutationFn: () => api.post<GenerateUnitsResult>('/units/generate', payload(false)),
    onSuccess: () => {
      setPreview(null);
      onDone();
    },
    onError,
  });

  return (
    <Card className="mt-4">
      <CardHeader
        title="Gerar o predio de uma vez"
        subtitle="Descreva a estrutura e confira a previa antes de gravar."
      />

      <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-5">
        {blocks.length > 0 && (
          <Field label="Bloco">
            <Select
              value={blockId}
              onChange={(next) => {
                setBlockId(next);
                setPreview(null);
              }}
              placeholder="Escolha o bloco..."
              options={blocks.map((block) => ({ value: block.id, label: block.name }))}
            />
          </Field>
        )}

        <Field label="Do andar">
          <TextInput
            value={floorFrom}
            inputMode="numeric"
            onChange={(event) => {
              setFloorFrom(event.target.value);
              setPreview(null);
            }}
          />
        </Field>

        <Field label="Ate o andar">
          <TextInput
            value={floorTo}
            inputMode="numeric"
            onChange={(event) => {
              setFloorTo(event.target.value);
              setPreview(null);
            }}
          />
        </Field>

        <Field label="Unidades por andar">
          <TextInput
            value={perFloor}
            inputMode="numeric"
            onChange={(event) => {
              setPerFloor(event.target.value);
              setPreview(null);
            }}
          />
        </Field>

        <Field label="Numeracao">
          <Select
            value={numbering}
            onChange={(next) => {
              setNumbering(next as 'floor_position' | 'sequential');
              setPreview(null);
            }}
            options={[
              { value: 'floor_position', label: 'Andar + posicao (801, 802)' },
              { value: 'sequential', label: 'Sequencial (1, 2, 3)' },
            ]}
          />
        </Field>
      </div>

      {preview && (
        <div className="mx-5 mb-5 rounded-card border border-accent/30 bg-accent/5 p-4">
          <p className="text-sm font-bold">
            {preview.created.length} unidade{preview.created.length === 1 ? '' : 's'} sera
            {preview.created.length === 1 ? '' : 'o'} criada
            {preview.created.length === 1 ? '' : 's'}
            {preview.skipped.length > 0 && ` · ${preview.skipped.length} ja existe(m)`}
          </p>

          <p className="mt-2 max-h-24 overflow-y-auto text-xs text-muted">
            {preview.created.join(', ') || 'Nada novo para criar.'}
          </p>

          {preview.remainingInPlan !== null && (
            <p className="mt-2 text-xs text-faint">
              Sobram {preview.remainingInPlan} unidades no plano depois desta operacao.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-line-soft p-5">
        <Button
          variant="ghost"
          loading={simulate.isPending}
          disabled={missingBlock}
          onClick={() => {
            clearError();
            simulate.mutate();
          }}
        >
          Ver previa
        </Button>

        {/* So libera a gravacao depois da previa: 200 apartamentos errados nao
            tem desfazer barato. */}
        <Button
          loading={commit.isPending}
          disabled={missingBlock || !preview || preview.created.length === 0}
          onClick={() => {
            clearError();
            commit.mutate();
          }}
        >
          {preview ? `Criar ${preview.created.length} unidades` : 'Criar unidades'}
        </Button>

        {missingBlock && (
          <p className="text-sm text-muted">Escolha em qual bloco as unidades ficam.</p>
        )}
      </div>
    </Card>
  );
}
